import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { cartTotalCents, useCart } from '../lib/cart';

interface CartUiContext {
  isOpen: boolean;
  open: () => void;
  close: () => void;
}
const Ctx = createContext<CartUiContext | null>(null);

export function useCartUi(): CartUiContext {
  // Allow NavBar to use it before provider wraps everything — fall back to events.
  const ctx = useContext(Ctx);
  if (ctx) return ctx;
  return {
    isOpen: false,
    open: () => window.dispatchEvent(new Event('rl:cart-open')),
    close: () => window.dispatchEvent(new Event('rl:cart-close')),
  };
}

export function CartDrawer() {
  const [cart, actions] = useCart();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const onOpen = () => setIsOpen(true);
    const onClose = () => setIsOpen(false);
    window.addEventListener('rl:cart-open', onOpen);
    window.addEventListener('rl:cart-close', onClose);
    return () => {
      window.removeEventListener('rl:cart-open', onOpen);
      window.removeEventListener('rl:cart-close', onClose);
    };
  }, []);

  const close = useCallback(() => setIsOpen(false), []);
  const total = useMemo(() => cartTotalCents(cart), [cart]);

  return (
    <>
      <div className={`cart-backdrop${isOpen ? ' open' : ''}`} onClick={close} />
      <aside className={`cart-drawer${isOpen ? ' open' : ''}`} aria-hidden={!isOpen}>
        <div className="header">
          <h3>Panier</h3>
          <button onClick={close} aria-label="Fermer">×</button>
        </div>
        <div className="items">
          {!cart || cart.items.length === 0 ? (
            <p className="muted" style={{ textAlign: 'center', marginTop: 40 }}>
              Votre panier est vide.
            </p>
          ) : (
            <>
              <p className="muted" style={{ marginBottom: 8 }}>
                Retrait chez <strong>{cart.shop_name}</strong>
              </p>
              {cart.items.map((it) => (
                <div className="item" key={it.product_id}>
                  <div className="thumb" style={{ backgroundImage: it.photo_url ? `url(${it.photo_url})` : undefined }} />
                  <div className="grow">
                    <div className="name">{it.name}</div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {(it.price_cents / 100).toFixed(2)} €
                    </div>
                    <div className="controls">
                      <button onClick={() => actions.setQuantity(it.product_id, it.quantity - 1)}>−</button>
                      <span>{it.quantity}</span>
                      <button onClick={() => actions.setQuantity(it.product_id, it.quantity + 1)}>+</button>
                      <button className="ghost" onClick={() => actions.setQuantity(it.product_id, 0)} style={{ marginLeft: 'auto' }}>
                        Retirer
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
        {cart && cart.items.length > 0 && (
          <div className="footer">
            <div className="total">
              <span>Total</span>
              <span>{(total / 100).toFixed(2)} €</span>
            </div>
            <Link to={`/panier/${cart.shop_slug}`} onClick={close} style={{ display: 'block' }}>
              <button className="primary" style={{ width: '100%' }}>Commander</button>
            </Link>
          </div>
        )}
      </aside>
    </>
  );
}
